import * as React from 'react';
import SearchField from '../../components/SearchField';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { interactiveRow } from '../../components/a11y';
import { StatusChip, fullName } from './shared';

/* Staff directory — roster with inline expansion (no modals). */

export default function StaffV2() {
    const {
        getStaff,
        getStaffMember,
        createStaffMember,
        deleteStaffMember,
        bulkCreateStaff,
    } = useProduction();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addFields, setAddFields] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });
    const [notice, setNotice] = useState<string | null>(null);
    const fileRef = React.useRef<HTMLInputElement>(null);

    const reload = async () => {
        const data = await getStaff().catch(() => []);
        setRows(data ?? []);
        setLoading(false);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await getStaff().catch(() => []);
            if (!mounted) return;
            setRows(data ?? []);
            setLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitAdd = async () => {
        if (!addFields.firstName.trim() || !addFields.email.trim()) return;
        const ok = await createStaffMember({
            firstName: addFields.firstName.trim(),
            lastName: addFields.lastName.trim() || null,
            email: addFields.email.trim(),
            phone: addFields.phone.trim() || null,
        })
            .then(() => true)
            .catch(() => false);
        setNotice(ok ? null : 'Add failed (duplicate email?)');
        if (ok) {
            setAddFields({ firstName: '', lastName: '', email: '', phone: '' });
            setAddOpen(false);
            await reload();
        }
    };

    /* CSV import — header row maps by name; array cells split on ; */
    const importCsv = async (file: File) => {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) return;
        const header = lines[0].split(',').map((h) => h.trim());
        const parsed = lines.slice(1).map((line) => {
            const cells = line.split(',');
            const row: Record<string, any> = {};
            header.forEach((key, i) => {
                const value = (cells[i] ?? '').trim();
                if (!value) return;
                if (['roles', 'skills', 'languages'].includes(key))
                    row[key] = value.split(';').map((v) => v.trim());
                else row[key] = value;
            });
            return row;
        });
        const result = await bulkCreateStaff(parsed).catch(() => null);
        setNotice(
            result
                ? `Imported ${result.created.length}; ${result.errors.length} skipped`
                : 'Import failed',
        );
        await reload();
    };

    const toggle = async (id: number) => {
        if (openId === id) {
            setOpenId(null);
            setDetail(null);
            return;
        }
        setOpenId(id);
        setDetail(null);
        const data = await getStaffMember(id).catch(() => null);
        setDetail(data);
    };

    const q = search.trim().toLowerCase();
    const filtered = rows.filter(
        (r) =>
            !q ||
            fullName(r).toLowerCase().includes(q) ||
            (r.email ?? '').toLowerCase().includes(q),
    );

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Jobs · Staff</div>

            <div className="mb-4 flex items-center gap-3">
                <span className="text-[13px] text-muted-foreground">
                    <b className="font-semibold text-foreground">
                        {filtered.length}
                    </b>{' '}
                    people
                </span>
                {notice && (
                    <span className="text-[12px] font-semibold text-attention">
                        {notice}
                    </span>
                )}
                <div className="flex-1" />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Search staff…"
                    className="w-full max-w-[260px]"
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                >
                    Import CSV
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) importCsv(file);
                        e.target.value = '';
                    }}
                />
                <button
                    type="button"
                    onClick={() => setAddOpen((o) => !o)}
                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-2 font-[inherit] text-[12.5px] font-semibold text-brand-foreground"
                >
                    Add person
                </button>
            </div>

            {addOpen && (
                <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-2xl border border-dashed border-border p-3">
                    <input
                        autoFocus
                        className="ps-input w-[130px] text-[12.5px]"
                        placeholder="First name"
                        value={addFields.firstName}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                firstName: e.target.value,
                            }))
                        }
                    />
                    <input
                        className="ps-input w-[130px] text-[12.5px]"
                        placeholder="Last name"
                        value={addFields.lastName}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                lastName: e.target.value,
                            }))
                        }
                    />
                    <input
                        className="ps-input w-[200px] text-[12.5px]"
                        placeholder="Email"
                        value={addFields.email}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                email: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitAdd();
                        }}
                    />
                    <input
                        className="ps-input w-[140px] text-[12.5px]"
                        placeholder="Phone"
                        value={addFields.phone}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                phone: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitAdd();
                        }}
                    />
                    <button
                        type="button"
                        disabled={
                            !addFields.firstName.trim() ||
                            !addFields.email.trim()
                        }
                        onClick={submitAdd}
                        className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}

            {loading && (
                <div>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                        />
                    ))}
                </div>
            )}
            {filtered.map((r) => (
                <React.Fragment key={r.id}>
                    <div
                        {...interactiveRow(() => toggle(+r.id))}
                        className="flex cursor-pointer items-center gap-3.5 border-t border-line px-0.5 py-[10px] text-[12.5px] first:border-t-0 hover:bg-sunk"
                    >
                        <span className="min-w-0 flex-1 basis-[140px] truncate font-semibold sm:flex-none sm:basis-auto sm:w-[190px]">
                            {fullName(r)}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {r.email}
                        </span>
                        <span className="w-[140px] flex-none truncate text-muted-foreground">
                            {(r.roles ?? []).join(', ') || '—'}
                        </span>
                        <span className="w-[130px] flex-none truncate text-faint">
                            {[r.city, r.state].filter(Boolean).join(', ') ||
                                '—'}
                        </span>
                        <span className="w-[90px] flex-none tabular-nums text-muted-foreground">
                            {r.assignmentCount ?? 0}{' '}
                            {+(r.assignmentCount ?? 0) === 1 ? 'gig' : 'gigs'}
                        </span>
                        <span className="flex-none text-faint">
                            {openId === +r.id ? '▾' : '›'}
                        </span>
                    </div>
                    {openId === +r.id && (
                        <div className="border-t border-line bg-sunk px-4 py-4 text-[12.5px]">
                            {!detail && (
                                <span className="text-faint">Loading…</span>
                            )}
                            {detail && (
                                <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
                                    <div>
                                        <div className="ps-label mb-1">
                                            Phone
                                        </div>
                                        {detail.phone ?? '—'}
                                    </div>
                                    <div>
                                        <div className="ps-label mb-1">
                                            Skills
                                        </div>
                                        {(detail.skills ?? []).join(', ') ||
                                            '—'}
                                    </div>
                                    <div>
                                        <div className="ps-label mb-1">
                                            Languages
                                        </div>
                                        {(detail.languages ?? []).join(', ') ||
                                            '—'}
                                    </div>
                                    <div>
                                        <div className="ps-label mb-1">
                                            Notes
                                        </div>
                                        {detail.notes ?? '—'}
                                    </div>
                                    <div className="col-span-2 flex items-start md:col-span-4">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (
                                                    !window.confirm(
                                                        `Delete ${fullName(r)}? This can't be undone.`,
                                                    )
                                                )
                                                    return;
                                                const ok =
                                                    await deleteStaffMember(
                                                        +r.id,
                                                    )
                                                        .then(() => true)
                                                        .catch(() => false);
                                                setNotice(
                                                    ok
                                                        ? null
                                                        : 'Cannot delete — has assignments',
                                                );
                                                if (ok) {
                                                    setOpenId(null);
                                                    await reload();
                                                }
                                            }}
                                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-attention"
                                        >
                                            Delete person
                                        </button>
                                    </div>
                                    <div className="col-span-2 md:col-span-4">
                                        <div className="ps-label mb-1.5">
                                            Assignment history
                                        </div>
                                        {(detail.assignments ?? []).length ===
                                            0 && (
                                            <span className="text-faint">
                                                None yet.
                                            </span>
                                        )}
                                        {(detail.assignments ?? []).map(
                                            (a: any) => (
                                                <div
                                                    key={a.id}
                                                    className="flex items-center gap-3 border-t border-line py-1.5 first:border-t-0"
                                                >
                                                    <span className="w-[64px] flex-none tabular-nums text-faint">
                                                        {a.startAt
                                                            ? moment(
                                                                  a.startAt,
                                                              ).format('MMM D')
                                                            : '—'}
                                                    </span>
                                                    <span className="min-w-0 flex-1 truncate font-semibold">
                                                        {a.activationName}
                                                    </span>
                                                    <span className="w-[110px] flex-none text-muted-foreground">
                                                        {a.role}
                                                    </span>
                                                    <StatusChip
                                                        status={a.status}
                                                    />
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </React.Fragment>
            ))}
        </main>
    );
}
